
import { Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
    CheckIsCodeExistsRequest,
    CommonClient,
    CreateCustomerRequest,
    CustomerClient,
    FileTypeEnum,
    ImageEnum,
    ObjectEnumOfCustomerTypeEnum,
    ObjectEnumOfGenderEnum
} from '../../shared/api-connector/sso-connector';
import { constant } from '../../shared/constants/constant';
import { AlertService } from '../../shared/services/alert.service';
import { UploadService } from '../../shared/services/upload.service';

// Import từ Base Form Module
import {
    FormSchema,
    FormSubmitEvent,
    AsyncValidationService,
    FieldOption,
    BaseFormComponent
} from '../../shared/modules/base-form';
import { DrawerRef } from '../../shared/components/drawer/drawer-ref';

@Component({
    selector: 'app-add-customer-drawer',
    templateUrl: './add-customer-drawer.component.html',
    styleUrls: ['./add-customer-drawer.component.scss']
})
export class AddCustomerDrawerComponent implements OnInit {
    // Drawer properties
    drawerRef!: DrawerRef;
    data: any;

    // Form schema
    customerFormSchema!: FormSchema;

    // Reference đến base form component
    @ViewChild(BaseFormComponent) formRef!: BaseFormComponent;

    // Data
    lstGender: FieldOption[] = [];
    lstCustomerType: FieldOption[] = [];

    // Avatar
    avatarId: string = '';
    isLoadingAvatar: boolean = false;
    @ViewChild('inputAvatar') $inputAvatar!: ElementRef;

    // States
    isLoading: boolean = false;
    maTinhThanh: string = '';
    maQuanHuyen: string = '';

    constructor(
        private spinner: NgxSpinnerService,
        private alertService: AlertService,
        private commonClient: CommonClient,
        private uploadService: UploadService,
        private customerClient: CustomerClient,
        private asyncValidation: AsyncValidationService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.loadFormData();
    }

    /**
     * Load data cho form (gender, customer type options)
     */
    loadFormData(): void {
        forkJoin([
            this.commonClient.getListGender(),
            this.commonClient.getListCustomer()
        ]).subscribe((result) => {
            const genders = result[0] || [];
            const customerTypes = result[1] || [];

            // Convert sang FieldOption format
            this.lstGender = genders.map((g: ObjectEnumOfGenderEnum) => ({
                label: g.description || '',
                value: g.value
            }));

            this.lstCustomerType = customerTypes.map((c: ObjectEnumOfCustomerTypeEnum) => ({
                label: c.description || '',
                value: c.value
            }));

            // Build schema sau khi có data
            this.buildFormSchema();
        });
    }

    /**
     * Build Form Schema
     * Đây là nơi DUY NHẤT bạn cần config form
     */
    buildFormSchema(): void {
        const codeExistsValidator = this.asyncValidation.createCodeExistsValidator(
            (code: string) => this.checkCodeExists(code),
            'Mã khách hàng đã tồn tại trên hệ thống'
        );

        // Generate default customer code
        const defaultCode = this.generateCustomerCode();

        this.customerFormSchema = {
            formId: 'add-customer-form',
            layout: {
                columns: 12, // Sử dụng hệ thống 12 cột thực sự
                labelPosition: 'top'
            },
            options: {
                showRequiredMark: true,
                validateOnBlur: true
            },
            fields: [
                // HÀNG 1: Mã + Tên + SĐT (3 cột)
                { key: 'code', type: 'text', label: 'Mã khách hàng', placeholder: 'Mã tự động', defaultValue: defaultCode, layout: { colSpan: 4 }, validation: { required: { value: true, message: 'Mã khách hàng là bắt buộc' }, asyncValidators: [codeExistsValidator] } },
                { key: 'name', type: 'custom', label: 'Tên khách hàng', placeholder: 'Nhập tên khách hàng', layout: { colSpan: 4 }, validation: { required: { value: true, message: 'Tên khách hàng là bắt buộc' } } },
                { key: 'phone', type: 'tel', label: 'Số điện thoại', placeholder: 'Ví dụ: 0901234567', layout: { colSpan: 4 }, validation: { required: { value: true, message: 'Số điện thoại là bắt buộc' }, preset: 'mobilephone' } },

                // HÀNG 2: SĐT phụ + Giới tính + Loại khách
                { key: 'phoneSub', type: 'tel', label: 'SĐT phụ', placeholder: 'SĐT phụ', layout: { colSpan: 4 }, validation: { preset: 'mobilephone' } },
                { key: 'gender', type: 'radio-group', label: 'Giới tính', options: this.lstGender, defaultValue: this.lstGender[0]?.value, layout: { colSpan: 4 } },
                { key: 'customerType', type: 'radio-group', label: 'Loại khách', options: this.lstCustomerType, defaultValue: this.lstCustomerType[0]?.value, layout: { colSpan: 4 } },

                // HÀNG 3: Ngày sinh + MST + Email
                { key: 'dateOfBirth', type: 'date', label: 'Ngày sinh', layout: { colSpan: 4 } },
                { key: 'taxCode', type: 'text', label: 'Mã số thuế', placeholder: 'Mã số thuế', layout: { colSpan: 4 } },
                { key: 'email', type: 'email', label: 'Email', placeholder: 'example@email.com', layout: { colSpan: 4 }, validation: { email: true } },

                // HÀNG 4: Facebook + Địa chỉ (Facebook 4, còn lại Address 8 hoặc Address xuống hàng)
                { key: 'facebook', type: 'text', label: 'Facebook', placeholder: 'Link hoặc username', layout: { colSpan: 4 } },
                { key: 'address', type: 'textarea', label: 'Địa chỉ', placeholder: 'Nhập địa chỉ chi tiết', layout: { colSpan: 8 }, attributes: { rows: 1 } },

                // HÀNG 5: Tỉnh/Huyện/Xã
                { key: 'tinhThanh', type: 'custom', label: 'Tỉnh/Thành', layout: { colSpan: 4 } },
                { key: 'quanHuyen', type: 'custom', label: 'Quận/Huyện', layout: { colSpan: 4 } },
                { key: 'phuongXa', type: 'custom', label: 'Phường/Xã', layout: { colSpan: 4 } },

                // HÀNG 6: Ghi chú (Full width)
                { key: 'note', type: 'textarea', label: 'Ghi chú', placeholder: 'Nhập ghi chú...', layout: { colSpan: 12 }, attributes: { rows: 2 } }

            ]

        };
    }

    /**
     * Khi form đã được khởi tạo xong
     */
    onFormReady(form: FormGroup): void {
        // Đồng bộ Tỉnh -> Huyện
        form.get('tinhThanh')?.valueChanges.subscribe(x => {
            this.maTinhThanh = (x && typeof x === 'object') ? (x.maTinhThanh || '') : '';
            form.get('quanHuyen')?.patchValue(null);
            form.get('phuongXa')?.patchValue(null);
            this.maQuanHuyen = '';
            this.cdr.detectChanges();

            // Auto-fill address
            this.buildAddressFromLocation(form);
        });

        // Đồng bộ Huyện -> Xã
        form.get('quanHuyen')?.valueChanges.subscribe(x => {
            this.maQuanHuyen = (x && typeof x === 'object') ? (x.maQuanHuyen || '') : '';
            form.get('phuongXa')?.patchValue(null);
            this.cdr.detectChanges();

            // Auto-fill address
            this.buildAddressFromLocation(form);
        });

        // Đồng bộ Xã -> Address
        form.get('phuongXa')?.valueChanges.subscribe(() => {
            this.buildAddressFromLocation(form);
        });
    }

    /**
     * Xử lý khi form thay đổi (các logic khác nếu cần)
     */
    onFormChange(event: any): void {
        // Logic đồng bộ địa điểm đã chuyển sang onFormReady để trực tiếp hơn
    }

    /**
     * Lấy mã tỉnh thành từ value
     */
    getMaTinhThanh(val: any): string {
        if (val && typeof val === 'object') {
            return val.maTinhThanh || '';
        }
        return '';
    }

    /**
     * Lấy mã quận huyện từ value
     */
    getMaQuanHuyen(val: any): string {
        if (val && typeof val === 'object') {
            return val.maQuanHuyen || '';
        }
        return '';
    }

    /**
     * Lấy mã phường xã từ value
     */
    getMaPhuongXa(val: any): string {
        if (val && typeof val === 'object') {
            return val.maPhuongXa || '';
        }
        return '';
    }

    /**
     * Tự động điền địa chỉ từ Tỉnh/Quận/Phường đã chọn
     */
    buildAddressFromLocation(form: FormGroup): void {
        const tinhThanh = form.get('tinhThanh')?.value;
        const quanHuyen = form.get('quanHuyen')?.value;
        const phuongXa = form.get('phuongXa')?.value;

        const parts: string[] = [];

        // Lấy tên Phường/Xã (nếu có)
        if (phuongXa && typeof phuongXa === 'object' && phuongXa.tenPhuongXa) {
            parts.push(phuongXa.tenPhuongXa);
        }

        // Lấy tên Quận/Huyện (nếu có)
        if (quanHuyen && typeof quanHuyen === 'object' && quanHuyen.tenQuanHuyen) {
            parts.push(quanHuyen.tenQuanHuyen);
        }

        // Lấy tên Tỉnh/Thành (nếu có)
        if (tinhThanh && typeof tinhThanh === 'object' && tinhThanh.tenTinhThanh) {
            parts.push(tinhThanh.tenTinhThanh);
        }

        // Ghép thành địa chỉ: "Phường/Xã, Quận/Huyện, Tỉnh/Thành"
        const address = parts.join(', ');

        // Chỉ set nếu có ít nhất 1 phần
        if (address) {
            form.get('address')?.patchValue(address, { emitEvent: false });
        }
    }

    /**
     * Generate mã khách hàng tự động: KH_timestamp
     * Ví dụ: KH_1737322283456
     */
    generateCustomerCode(): string {
        const timestamp = new Date().getTime();
        return `KH_${timestamp}`;
    }


    /**
     * Check mã khách hàng đã tồn tại
     */
    checkCodeExists(code: string): Observable<boolean> {
        return this.customerClient.checkIsCodeExists(
            new CheckIsCodeExistsRequest({ code })
        );
    }

    /**
     * Xử lý submit form
     */
    onFormSubmit(event: FormSubmitEvent): void {
        if (!event.isValid) return;

        const values = event.values;

        this.isLoading = true;
        this.spinner.show();

        this.customerClient.createOrUpdate(new CreateCustomerRequest({
            id: 0,
            name: values.name,
            code: values.code,
            phone: values.phone,
            phoneSub: values.phoneSub,
            tinhThanhCode: this.getMaTinhThanh(values.tinhThanh),
            quanHuyenCode: this.getMaQuanHuyen(values.quanHuyen),
            phuongXaCode: this.getMaPhuongXa(values.phuongXa),
            dateOfBirth: values.dateOfBirth,
            maSoThue: values.taxCode,
            email: values.email,
            facebook: values.facebook,
            note: values.note,
            avartaId: this.avatarId,
            type: values.customerType,
            address: values.address,
            gender: values.gender,
        }))


            .pipe(finalize(() => {
                this.isLoading = false;
                this.spinner.hide();
            }))
            .subscribe({
                next: (result: any) => {
                    this.alertService.success('Thêm mới khách hàng thành công', 'Thành công');
                    if (this.drawerRef) {
                        this.drawerRef.close(result);
                    }
                },
                error: (_err: any) => {
                    this.alertService.error('Có lỗi xảy ra khi thêm khách hàng');
                }
            });
    }

    /**
     * Đóng dialog
     */
    onCancel(): void {
        if (this.drawerRef) {
            this.drawerRef.close();
        }
    }

    /**
     * Trigger submit form từ external button
     */
    submitForm(): void {
        if (this.formRef) {
            this.formRef.onSubmit();
        }
    }

    /**
     * Upload avatar
     */
    onChangeFileAvatar(event: any): void {
        const file = event.target.files[0];
        if (this.$inputAvatar) {
            this.$inputAvatar.nativeElement.value = '';
        }

        if (!file) return;

        this.isLoadingAvatar = true;

        if (!file.type.toLowerCase().includes('image/')) {
            this.alertService.error('Sai định dạng ảnh. Mời bạn nhập lại');
            this.isLoadingAvatar = false;
            return;
        }

        if (file.size > constant.SIZE_IMG) {
            this.alertService.error('Tệp ảnh phải nhỏ hơn 10mb');
            this.isLoadingAvatar = false;
            return;
        }

        this.uploadService.uploadFile(file, FileTypeEnum.AvatarAccount, ImageEnum.AvatarType)
            .pipe(finalize(() => this.isLoadingAvatar = false))
            .subscribe((x: any) => {
                if (x.isSuccess) {
                    this.avatarId = x.id;
                } else {
                    this.alertService.error('File upload không đúng định dạng', 'Lỗi upload file');
                }
            });
    }

    getUrlFile(id: string): string {
        return this.uploadService.getUrlFile(id);
    }
}
